import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Utensils, Clock, FileText, Lock, Download, ChevronDown, ChevronUp, Flame, Dumbbell, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NutritionPlan = () => {
  const { level } = useParams();
  const [nutritionData, setNutritionData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState('masa'); // 'masa' o 'grasa' para nivel Premium

  useEffect(() => {
    fetchData();
  }, [level]);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    
    try {
      if (token) {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data);
      }

      const nutritionRes = await axios.get(`${BACKEND_URL}/api/program-content/${level}/nutrition`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNutritionData(nutritionRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('no_access');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getLevelName = () => {
    const names = {
      'nivel_1': 'Nivel 1 - Principiante',
      'nivel_2': 'Nivel 2 - Intermedio',
      'nivel_3': 'Nivel 3 - Avanzado'
    };
    return names[level] || level;
  };

  const getLevelColor = () => {
    const colors = {
      'nivel_1': 'bg-blue-600',
      'nivel_2': 'bg-purple-600',
      'nivel_3': 'bg-orange-600'
    };
    return colors[level] || 'bg-pink-600';
  };

  const getNutritionInfo = () => {
    const info = {
      'nivel_1': {
        title: 'Plan Nutricional Personalizado',
        description: 'Completa el formulario para recibir tu plan personalizado según tus objetivos.'
      },
      'nivel_2': {
        title: '5 Opciones de Menú',
        description: 'Elige entre 5 menús diferentes adaptados a diferentes gustos y necesidades.'
      },
      'nivel_3': {
        title: '6 Menús Especializados',
        description: '3 menús para masa muscular + 3 menús para reducción de grasa corporal.'
      }
    };
    return info[level] || info['nivel_1'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error === 'no_access') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-3xl p-8 max-w-md text-center border border-zinc-800">
          <Lock className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Contenido Bloqueado</h1>
          <p className="text-zinc-400 mb-6">
            No tienes acceso a este plan nutricional. Actualiza tu suscripción para desbloquear este contenido.
          </p>
          <Link to="/programs">
            <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8">
              Ver Programas
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const nutritionInfo = getNutritionInfo();

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-zinc-400 hover:text-green-600 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${getLevelColor()}`}>
              {getLevelName()}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
            <Utensils className="w-8 h-8 text-green-600" />
            {nutritionInfo.title}
          </h1>
          
          <p className="text-zinc-400 mt-2">{nutritionInfo.description}</p>
          
          {nutritionData?.description && (
            <div className="mt-4 p-4 bg-green-50 rounded-xl text-green-700">
              {nutritionData.description}
            </div>
          )}
        </div>

        {/* Sin contenido */}
        {(!nutritionData?.plans || nutritionData.plans.length === 0) && (
          <div className="bg-zinc-900 rounded-3xl p-8 text-center border border-zinc-800">
            <Utensils className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Plan Nutricional Próximamente</h2>
            <p className="text-zinc-400 mb-6">
              Tu plan nutricional está siendo preparado por nuestras especialistas. ¡Vuelve pronto!
            </p>
            
            {level === 'nivel_1' && (
              <div className="bg-blue-50 rounded-xl p-6 text-left max-w-md mx-auto">
                <h3 className="font-bold text-blue-700 mb-2">Mientras tanto...</h3>
                <p className="text-sm text-blue-600">
                  Puedes completar el formulario de evaluación nutricional para que podamos personalizar tu plan.
                </p>
                <Link to="/nutrition-form">
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                    Completar Formulario
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Lista de planes */}
        {nutritionData?.plans && nutritionData.plans.length > 0 && (
          <div className="space-y-6">
            {/* Selector de objetivo para nivel Premium (nivel_3) */}
            {level === 'nivel_3' && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-pink-500" />
                  <h3 className="font-bold text-lg">¿Cuál es tu objetivo actual?</h3>
                </div>
                <p className="text-zinc-400 text-sm mb-4">
                  Selecciona tu objetivo para ver los menús y calorías correspondientes
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setSelectedGoal('masa')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      selectedGoal === 'masa'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedGoal === 'masa' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        <Dumbbell className="w-5 h-5" />
                      </div>
                      <span className={`font-bold ${selectedGoal === 'masa' ? 'text-blue-400' : 'text-white'}`}>
                        💪 Aumentar Masa Muscular
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Superávit calórico (+350 cal) • Más carbohidratos para energía y recuperación
                    </p>
                  </button>
                  
                  <button
                    onClick={() => setSelectedGoal('grasa')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      selectedGoal === 'grasa'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedGoal === 'grasa' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        <Flame className="w-5 h-5" />
                      </div>
                      <span className={`font-bold ${selectedGoal === 'grasa' ? 'text-orange-400' : 'text-white'}`}>
                        🔥 Reducir % de Grasa
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Déficit calórico (-450 cal) • Más proteína para preservar músculo
                    </p>
                  </button>
                </div>
              </div>
            )}
            
            {/* Filtrar planes según objetivo seleccionado (solo para Premium) */}
            {nutritionData.plans
              .filter(plan => {
                // Si es nivel Premium, filtrar por tipo de menú según objetivo
                if (level === 'nivel_3') {
                  const planType = plan.menu_type || plan.type;
                  if (selectedGoal === 'masa') {
                    return planType === 'masa' || planType === 'mass_gain';
                  } else {
                    return planType === 'grasa' || planType === 'fat_loss';
                  }
                }
                // Para otros niveles, mostrar todos
                return true;
              })
              .map((plan, idx) => (
              <div key={idx} className={`bg-zinc-900 rounded-2xl border overflow-hidden ${
                level === 'nivel_3' 
                  ? selectedGoal === 'masa' ? 'border-blue-500/30' : 'border-orange-500/30'
                  : 'border-zinc-800'
              }`}>
                {/* Header del plan con calorías */}
                {level === 'nivel_3' && plan.calories && (
                  <div className={`px-4 py-3 ${
                    selectedGoal === 'masa' ? 'bg-blue-500/10' : 'bg-orange-500/10'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        selectedGoal === 'masa' ? 'text-blue-400' : 'text-orange-400'
                      }`}>
                        {selectedGoal === 'masa' ? '💪 Objetivo: Masa Muscular' : '🔥 Objetivo: Reducir Grasa'}
                      </span>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-white font-bold">{plan.calories} cal</span>
                        {plan.macros && (
                          <>
                            <span className="text-zinc-400">P: {plan.macros.proteinas_g}g</span>
                            <span className="text-zinc-400">C: {plan.macros.carbohidratos_g}g</span>
                            <span className="text-zinc-400">G: {plan.macros.grasas_g}g</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <div 
                  onClick={() => setExpandedPlan(expandedPlan === idx ? -1 : idx)}
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#09090b]"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      (plan.menu_type === 'masa' || plan.type === 'mass_gain') ? 'bg-blue-500/20 text-blue-400' :
                      (plan.menu_type === 'grasa' || plan.type === 'fat_loss') ? 'bg-orange-500/20 text-orange-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {(plan.menu_type === 'masa' || plan.type === 'mass_gain') ? <Dumbbell className="w-6 h-6" /> :
                       (plan.menu_type === 'grasa' || plan.type === 'fat_loss') ? <Flame className="w-6 h-6" /> :
                       <Utensils className="w-6 h-6" />}
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{plan.name}</h3>
                      <p className="text-sm text-zinc-500">
                        {(plan.menu_type === 'masa' || plan.type === 'mass_gain') ? 'Para ganar masa muscular' :
                         (plan.menu_type === 'grasa' || plan.type === 'fat_loss') ? 'Para reducir grasa' :
                         `${plan.meals?.length || 0} comidas`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {plan.pdf_url && (
                      <a 
                        href={plan.pdf_url.startsWith('http') ? plan.pdf_url : `${BACKEND_URL}${plan.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                      >
                        <Download className="w-5 h-5" />
                      </a>
                    )}
                    {expandedPlan === idx ? (
                      <ChevronUp className="w-5 h-5 text-zinc-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                </div>
                
                {expandedPlan === idx && plan.meals && plan.meals.length > 0 && (
                  <div className="border-t border-zinc-800">
                    <div className="p-4 bg-[#09090b]">
                      <h4 className="font-medium text-sm text-zinc-500 mb-3">Distribución de Comidas</h4>
                      <div className="space-y-3">
                        {plan.meals.map((meal, mealIdx) => (
                          <div key={mealIdx} className="bg-zinc-900 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-white">{meal.name}</span>
                              {meal.time && (
                                <span className="text-sm text-zinc-500 flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {meal.time}
                                </span>
                              )}
                            </div>
                            <p className="text-zinc-400 text-sm">{meal.foods}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {expandedPlan === idx && (!plan.meals || plan.meals.length === 0) && plan.pdf_url && (
                  <div className="p-6 border-t border-zinc-800 text-center">
                    <FileText className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-zinc-400 mb-4">Este plan está disponible en formato PDF</p>
                    <a 
                      href={plan.pdf_url.startsWith('http') ? plan.pdf_url : `${BACKEND_URL}${plan.pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-medium"
                    >
                      <Download className="w-5 h-5" />
                      Descargar PDF
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Información adicional por nivel */}
        <div className="mt-8 bg-zinc-900 rounded-3xl p-6 border border-zinc-800">
          <h2 className="text-xl font-bold mb-4">Información Importante</h2>
          
          {level === 'nivel_1' && (
            <div className="space-y-3 text-zinc-400">
              <p>• Tu plan se personaliza basado en tus objetivos y características físicas.</p>
              <p>• Recibirás recomendaciones generales de alimentación.</p>
              <p>• Cualquier duda puedes escribirnos al correo de soporte.</p>
            </div>
          )}
          
          {level === 'nivel_2' && (
            <div className="space-y-3 text-zinc-400">
              <p>• Tienes acceso a 5 menús diferentes que puedes alternar durante la semana.</p>
              <p>• Cada menú está diseñado para complementar tu entrenamiento.</p>
              <p>• Incluye opciones para diferentes gustos alimenticios.</p>
            </div>
          )}
          
          {level === 'nivel_3' && (
            <div className="space-y-3 text-zinc-400">
              <p>• <strong>3 menús para masa muscular:</strong> Mayor aporte calórico y proteico para construcción muscular.</p>
              <p>• <strong>3 menús para reducción de grasa:</strong> Déficit calórico controlado manteniendo la masa muscular.</p>
              <p>• Elige el tipo de menú según tu objetivo actual.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NutritionPlan;
